import { ApolloClient, ApolloError, gql, useApolloClient } from '@apollo/client'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import { useCallback, useState } from 'react'

import { getCustomerTokenHeaderObject } from '@emico/login-token'

import { Address, fragments, getAddressessQuery } from './getAddresses.query'
import {
    CustomerAddressInput,
    Maybe,
    MutationUpdateCustomerAddressArgs,
} from '../graphql/schema.generated'

const UPDATE_ADDRESS = gql`
    mutation UpdateCustomerAddress($id: Int!, $input: CustomerAddressInput!) {
        customerAddress: updateCustomerAddress(id: $id, input: $input) {
            ...Address
        }
    }
    ${fragments.address}
`

export interface UpdateAddressData {
    customerAddress: Maybe<Address>
}

export type UpdateAddressVariables = MutationUpdateCustomerAddressArgs

export const useUpdateCustomerAddress = () => {
    const client = useApolloClient()
    const context = useAuthorizationContext()
    const [loading, setLoading] = useState<boolean>(false)

    const updateAddress = useCallback(
        async (
            id: number,
            address: Partial<Record<keyof Address, any>> | CustomerAddressInput,
        ): Promise<Address | undefined | null> => {
            setLoading(true)
            try {
                const { data } = await client
                    .mutate<UpdateAddressData>({
                        mutation: UPDATE_ADDRESS,
                        variables: { id, input: address },
                        context,
                        refetchQueries: () => [
                            {
                                query: getAddressessQuery,
                                context,
                            },
                        ],
                        awaitRefetchQueries: true,
                    })
                    .finally(() => setLoading(false))
                return data?.customerAddress
            } catch (err: any) {
                if (err instanceof ApolloError) {
                    const errorMessage = err.graphQLErrors?.[0]?.message
                    throw new Error(errorMessage)
                }
                throw err
            }
        },
        [client, context],
    )
    return { updateAddress, loading }
}

export const updateAddressTelephone = async (
    client: ApolloClient<unknown>,
    token: string | null,
    id: number,
    phone: string,
) =>
    client.mutate({
        mutation: UPDATE_ADDRESS,
        variables: {
            id,
            input: {
                telephone: phone,
            },
        },
        context: getCustomerTokenHeaderObject(token),
    })

export default UPDATE_ADDRESS
